import React, { useState } from 'react';
import { Camera, X, Instagram, Facebook, Youtube } from 'lucide-react';

const Gallery = () => {
    const [filter, setFilter] = useState('All');
    const [selectedImage, setSelectedImage] = useState(null);

    // Categories
    const categories = ['All', 'Events', 'Campus', 'Sports', 'Academic', 'Arts'];

    // Robust Image Replacements (Unsplash IDs)
    const galleryData = [
        { id: 1, cat: 'Events', src: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Annual Cultural Fest', desc: 'A vibrant display of student talent.' },
        { id: 2, cat: 'Campus', src: 'https://images.unsplash.com/photo-1525921429624-479b6a26d84d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'School Entrance', desc: 'The welcoming gateway to knowledge.' },
        { id: 3, cat: 'Sports', src: 'https://images.unsplash.com/photo-1576610616656-d3aa5d1f4534?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Annual Sports Meet', desc: 'Students competing in swimming.' },
        { id: 4, cat: 'Academic', src: 'https://images.unsplash.com/photo-1577896851231-70ef18881754?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Library Session', desc: 'Students engrossed in reading.' },
        { id: 5, cat: 'Arts', src: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Painting Workshop', desc: 'Unleashing creativity on canvas.' },
        { id: 6, cat: 'Events', src: 'https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Lighting Ceremony', desc: 'Inaugurating the new academic year.' },
        { id: 7, cat: 'Campus', src: 'https://images.unsplash.com/photo-1497294815431-9365093b7331?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Classroom', desc: 'Modern classrooms with smart boards.' },
        { id: 8, cat: 'Sports', src: 'https://images.unsplash.com/photo-1543351611-58f69d7c1781?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Football Practice', desc: 'Training session in progress.' },
        { id: 9, cat: 'Academic', src: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Science Exhibition', desc: 'Showcasing innovative project models.' },
        { id: 10, cat: 'Arts', src: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Music Room', desc: 'Students learning instruments.' },
        { id: 11, cat: 'Events', src: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Convocation', desc: 'Celebrating the graduating batch.' },
        { id: 12, cat: 'Campus', src: 'https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Playground', desc: 'A vast area for recreation.' },
    ];

    const filteredImages = filter === 'All' ? galleryData : galleryData.filter(img => img.cat === filter);

    return (
        <div className="font-sans text-gray-800 bg-white pt-28">
            {/* Header */}
            <div className="bg-gray-900 text-white py-24 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
                <div className="container relative z-10">
                    <div className="w-16 h-16 bg-secondary text-primary rounded-full flex items-center justify-center mx-auto mb-6">
                        <Camera size={32} />
                    </div>
                    <h1 className="text-5xl font-black mb-4">TCM Gallery</h1>
                    <p className="max-w-xl mx-auto text-gray-400">Glimpses of life at TCM Matriculation School. From academic triumphs to cultural extravaganzas.</p>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="container py-12 sticky top-20 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100">
                <div className="flex flex-wrap justify-center gap-3">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setFilter(cat)}
                            className={`px-6 py-2 rounded-full font-bold text-sm transition-all border ${filter === cat
                                ? 'bg-primary text-white border-primary shadow-lg scale-105'
                                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                                }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Gallery Grid */}
            <div className="container pb-24 min-h-[50vh]">
                <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                    {filteredImages.map((img) => (
                        <div key={img.id} className="break-inside-avoid relative group rounded-2xl overflow-hidden cursor-zoom-in" onClick={() => setSelectedImage(img)}>
                            <img src={img.src} alt={img.title} className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                                <span className="text-secondary text-xs font-bold uppercase tracking-wider mb-1">{img.cat}</span>
                                <h3 className="text-white font-bold text-xl">{img.title}</h3>
                                <p className="text-gray-300 text-sm mt-1 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all delay-100">{img.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Lightbox Modal */}
            {selectedImage && (
                <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
                    <button className="absolute top-6 right-6 text-white hover:text-secondary transition-colors" onClick={() => setSelectedImage(null)}>
                        <X size={40} />
                    </button>
                    <div className="max-w-5xl w-full max-h-[90vh] bg-transparent flex flex-col items-center" onClick={e => e.stopPropagation()}>
                        <img src={selectedImage.src} alt={selectedImage.title} className="max-h-[80vh] w-auto rounded-lg shadow-2xl" />
                        <div className="mt-6 text-center text-white">
                            <h3 className="text-2xl font-bold">{selectedImage.title}</h3>
                            <p className="text-gray-400 mt-2">{selectedImage.desc}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Social Connect */}
            <div className="bg-primary text-white py-16">
                <div className="container text-center">
                    <h2 className="text-3xl font-black mb-8">Follow Our Stories</h2>
                    <div className="flex justify-center gap-8">
                        <a href="https://www.instagram.com/tcm_matriculation_school/" className="flex flex-col items-center gap-2 group">
                            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-pink-600 transition-colors">
                                <Instagram size={32} />
                            </div>
                            <span className="font-bold text-sm">Instagram</span>
                        </a>
                        <a href="https://www.facebook.com/profile.php?id=61578302762657" className="flex flex-col items-center gap-2 group">
                            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                                <Facebook size={32} />
                            </div>
                            <span className="font-bold text-sm">Facebook</span>
                        </a>
                        <a href="https://youtube.com" className="flex flex-col items-center gap-2 group">
                            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-red-600 transition-colors">
                                <Youtube size={32} />
                            </div>
                            <span className="font-bold text-sm">YouTube</span>
                        </a>
                    </div>
                </div>
            </div>

        </div>
    );
};
export default Gallery;
